/**
 * TenantGridTab (G-3) — 본 스펙 MONITORING_SPEC.md §4.
 *
 * 8 테넌트 한 화면 요약. 첫 진입 5초 안에 "다 초록?" 판단.
 * 행 클릭 시 그 테넌트의 prompt 단위 인라인 전개 (실패 행 + 진행 prompt).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { fetchSummary, fetchFailures } from "../api/monitoring";
import type { FailureRow, FixLoopStatus, TenantSummary } from "../api/monitoring";

const STATUS_COLOR: Record<FixLoopStatus, "success" | "error" | "warning" | "info" | "default"> = {
  escalated: "error",
  failed: "error",
  running: "info",
  pending: "warning",
  success: "success",
};

interface Props {
  bsda: string;
}

export function TenantGridTab({ bsda }: Props) {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [allFailures, setAllFailures] = useState<FailureRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [summary, failures] = await Promise.all([
        fetchSummary(bsda),
        fetchFailures(bsda),
      ]);
      setTenants(summary.tenants);
      setAllFailures(failures.failures);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [bsda]);

  useEffect(() => {
    void reload();
    const t = setInterval(() => void reload(), 30_000);
    return () => clearInterval(t);
  }, [reload]);

  const failuresByTenant = useMemo(() => {
    const m = new Map<string, FailureRow[]>();
    for (const f of allFailures) {
      const arr = m.get(f.tenant_id) || [];
      arr.push(f);
      m.set(f.tenant_id, arr);
    }
    return m;
  }, [allFailures]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>테넌트</TableCell>
              <TableCell align="right">prompts</TableCell>
              <TableCell align="center">결과</TableCell>
              <TableCell align="center">fix_loop</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && tenants.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {tenants.map((t) => {
              const isExpanded = expanded.has(t.tenant_id);
              const fails = failuresByTenant.get(t.tenant_id) || [];
              const allGreen = t.prompts.failed === 0 && t.prompts.partial === 0;
              return (
                <TenantRow
                  key={t.tenant_id}
                  tenant={t}
                  isExpanded={isExpanded}
                  failures={fails}
                  allGreen={allGreen}
                  onToggle={() => toggle(t.tenant_id)}
                />
              );
            })}
            {!loading && tenants.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    이 날짜에 변환 데이터가 없습니다 ({bsda})
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

interface TenantRowProps {
  tenant: TenantSummary;
  isExpanded: boolean;
  failures: FailureRow[];
  allGreen: boolean;
  onToggle: () => void;
}

function TenantRow({ tenant, isExpanded, failures, allGreen, onToggle }: TenantRowProps) {
  const t = tenant;
  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: "pointer",
          bgcolor: allGreen ? undefined : "action.hover",
          "& > *": { borderBottom: "unset" },
        }}
        onClick={onToggle}
      >
        <TableCell>
          <IconButton size="small" aria-label="expand row">
            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {t.tenant_name_korean}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({t.tenant_id})
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{t.prompts.total}</Typography>
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Chip size="small" label={`✅ ${t.prompts.success}`} color="success" variant="outlined" />
            {t.prompts.partial > 0 && (
              <Chip size="small" label={`⚠️ ${t.prompts.partial}`} color="warning" variant="outlined" />
            )}
            {t.prompts.failed > 0 && (
              <Chip size="small" label={`❌ ${t.prompts.failed}`} color="error" />
            )}
          </Stack>
        </TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Chip size="small" label={`완료 ${t.fix_loop.completed}`} variant="outlined" />
            {t.fix_loop.running > 0 && (
              <Chip size="small" label={`진행 ${t.fix_loop.running}`} color="info" />
            )}
            {t.fix_loop.failed > 0 && (
              <Chip size="small" label={`실패 ${t.fix_loop.failed}`} color="error" />
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0 }} colSpan={5}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: "background.default" }}>
              {failures.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  이 테넌트의 그날 BSDA 에는 실패/진행 행이 없습니다.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>prompt_id</TableCell>
                      <TableCell>status</TableCell>
                      <TableCell align="right">fail JSON</TableCell>
                      <TableCell align="right">fix 시도</TableCell>
                      <TableCell>마지막 fix_pattern</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failures.map((f) => (
                      <TableRow key={`${f.tenant_id}-${f.prompt_id}`}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {f.prompt_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={f.status} color={STATUS_COLOR[f.status]} />
                        </TableCell>
                        <TableCell align="right">{f.fail_count}</TableCell>
                        <TableCell align="right">{f.fix_attempt_count}</TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {f.last_fix_pattern || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
